import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Calculator, Info, TrendingUp, PieChart, IndianRupee, Calendar } from 'lucide-react';

const LiveSIPAnalysisPage = () => {
  const [amcList, setAmcList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAmc, setSelectedAmc] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const [inputs, setInputs] = useState({
    monthly_amount: 10000,
    start_date: '2020-01-01',
    scheme_return: 12.0
  });
  
  const [result, setResult] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [amcRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/research/amc-list`),
        fetch(`${API_URL}/api/research/scheme-categories`)
      ]);
      
      const amcData = await amcRes.json();
      const catData = await catRes.json();
      
      if (amcData.status === 'success') setAmcList(amcData.data);
      if (catData.status === 'success') setCategories(catData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const calculateSIP = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/research/sip-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs)
      });
      const data = await res.json();
      if (data.status === 'success') {
        setResult(data.data);
      }
    } catch (error) {
      console.error('Error calculating SIP:', error);
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const presetDates = [
    { label: '1 Year Ago', date: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0] },
    { label: '3 Years Ago', date: new Date(new Date().setFullYear(new Date().getFullYear() - 3)).toISOString().split('T')[0] },
    { label: '5 Years Ago', date: new Date(new Date().setFullYear(new Date().getFullYear() - 5)).toISOString().split('T')[0] },
    { label: '10 Years Ago', date: new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0] },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mb-4">
              <Calculator className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Live SIP Analysis
            </h1>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm text-left">
                  Calculate how your SIP investment would have grown based on historical returns. 
                  Select a scheme, enter your SIP amount, and start date to see the analysis.
                </p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-blue-400" />
                SIP Details
              </h2>

              {/* AMC & Category Filters */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Fund House</label>
                  <select
                    value={selectedAmc}
                    onChange={(e) => setSelectedAmc(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500 appearance-none cursor-pointer text-sm"
                  >
                    <option value="all" className="bg-gray-900">All Fund Houses</option>
                    {amcList.map((amc, idx) => (
                      <option key={idx} value={amc} className="bg-gray-900">{amc}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Scheme Type</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500 appearance-none cursor-pointer text-sm"
                  >
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat.value} className="bg-gray-900">{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-6">
                {/* Monthly Amount */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-white font-medium">Monthly SIP Amount</label>
                    <span className="text-blue-400 font-bold text-lg">₹{inputs.monthly_amount.toLocaleString('en-IN')}</span>
                  </div>
                  <input
                    type="range"
                    min="500"
                    max="100000"
                    step="500"
                    value={inputs.monthly_amount}
                    onChange={(e) => setInputs({...inputs, monthly_amount: parseInt(e.target.value)})}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-1">
                    <span>₹500</span>
                    <span>₹1,00,000</span>
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <label className="text-white font-medium mb-2 block">SIP Start Date</label>
                  <input
                    type="date"
                    value={inputs.start_date}
                    onChange={(e) => setInputs({...inputs, start_date: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {presetDates.map((preset, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInputs({...inputs, start_date: preset.date})}
                        className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-white/70 hover:bg-white/10 transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Expected Return */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-white font-medium">Expected Return (CAGR)</label>
                    <span className="text-green-400 font-bold text-lg">{inputs.scheme_return}%</span>
                  </div>
                  <input
                    type="range"
                    min="6"
                    max="25"
                    step="0.5"
                    value={inputs.scheme_return}
                    onChange={(e) => setInputs({...inputs, scheme_return: parseFloat(e.target.value)})}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-1">
                    <span>6%</span>
                    <span>25%</span>
                  </div>
                </div>

                <button
                  onClick={calculateSIP}
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? 'Calculating...' : 'Calculate SIP Returns'}
                </button>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              {result ? (
                <>
                  {/* Main Result */}
                  <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-3xl p-8 border border-blue-500/30">
                    <p className="text-white/60 text-sm mb-2">Current Portfolio Value</p>
                    <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.current_value)}</p>
                    <p className="text-green-400 text-lg">
                      +{formatCurrency(result.returns)} ({result.returns_percent}% returns)
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <IndianRupee className="w-4 h-4 text-white/50" />
                        <p className="text-white/50 text-xs">Total Invested</p>
                      </div>
                      <p className="text-2xl font-bold text-white">{formatCurrency(result.total_invested)}</p>
                      <p className="text-white/50 text-xs mt-1">{result.total_months} installments</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <p className="text-white/50 text-xs">XIRR / CAGR</p>
                      </div>
                      <p className="text-2xl font-bold text-green-400">{result.cagr}%</p>
                      <p className="text-white/50 text-xs mt-1">Annualized return</p>
                    </div>
                  </div>

                  {/* Visual Breakdown */}
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <p className="text-white font-semibold mb-4 flex items-center gap-2">
                      <PieChart className="w-4 h-4 text-blue-400" />
                      Investment Breakdown
                    </p>
                    <div className="h-4 bg-white/10 rounded-full overflow-hidden flex">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                        style={{width: `${(result.total_invested / result.current_value) * 100}%`}}
                      ></div>
                      <div 
                        className="bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                        style={{width: `${(result.returns / result.current_value) * 100}%`}}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-blue-500"></div>
                        <span className="text-white/70">Principal: {((result.total_invested / result.current_value) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-green-500"></div>
                        <span className="text-white/70">Returns: {((result.returns / result.current_value) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Breakdown Table */}
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <p className="text-white font-semibold mb-4">Summary</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Monthly SIP</span>
                        <span className="text-white font-medium">₹{result.monthly_sip.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Investment Duration</span>
                        <span className="text-white font-medium">{result.total_months} months ({(result.total_months / 12).toFixed(1)} years)</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Total Amount Invested</span>
                        <span className="text-white font-medium">{formatCurrency(result.total_invested)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Wealth Gained</span>
                        <span className="text-green-400 font-medium">+{formatCurrency(result.returns)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-white font-medium">Current Value</span>
                        <span className="text-blue-400 font-bold text-lg">{formatCurrency(result.current_value)}</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10 text-center">
                  <Calculator className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Enter SIP details and click Calculate to see your returns analysis</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LiveSIPAnalysisPage;
